import { useState, useEffect } from 'react';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../services/firebase';

export interface JiraProject {
  id: string;
  key: string;
  name: string;
}

export interface JiraTicket {
  id: string;
  key: string;
  summary: string;
  description?: string;
  status: string;
  priority?: string;
  assignee?: string;
}

export interface JiraConfig {
  clientId: string;
  clientSecret: string;
  redirectUri: string;
  cloudId?: string;
  baseUrl?: string;
  accessToken?: string;
  refreshToken?: string;
  tokenExpiresAt?: number;
}

export interface AccessibleSite {
  id: string;
  name: string;
  url: string;
}

export function useJira() {
  const [config, setConfig] = useState<JiraConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const configRef = doc(db, 'config/jira');
      const configSnap = await getDoc(configRef);
      
      if (configSnap.exists()) {
        setConfig(configSnap.data() as JiraConfig);
      } else {
        setConfig(null);
      }
      setError(null);
    } catch (err) {
      console.error('Error loading Jira config:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async (newConfig: JiraConfig) => {
    try {
      const configRef = doc(db, 'config/jira');
      await setDoc(configRef, newConfig, { merge: true });
      setConfig(newConfig);
    } catch (err) {
      console.error('Error saving Jira config:', err);
      throw err;
    }
  };

  const initiateOAuthFlow = (): string => {
    if (!config?.clientId) {
      throw new Error('Jira OAuth configuration incomplete');
    }

    const state = Math.random().toString(36).substring(7);
    sessionStorage.setItem('jira_oauth_state', state);

    const scope = 'read:jira-work write:jira-work manage:jira-project manage:jira-configuration manage:jira-webhook manage:jira-data-provider offline_access';
    const authBaseUrl = 'https://auth.atlassian.com/authorize';

    const params = new URLSearchParams({
      audience: 'api.atlassian.com',
      client_id: config.clientId,
      scope: scope,
      redirect_uri: config.redirectUri,
      state: state,
      response_type: 'code',
      prompt: 'consent',
    });

    return `${authBaseUrl}?${params.toString()}`;
  };

  const handleOAuthCallback = async (code: string): Promise<void> => {
    try {
      const savedState = sessionStorage.getItem('jira_oauth_state');
      const currentState = new URLSearchParams(window.location.search).get('state');

      if (!savedState || savedState !== currentState) {
        throw new Error('Invalid state parameter. Possible CSRF attack.');
      }

      sessionStorage.removeItem('jira_oauth_state');

      if (!config?.clientId || !config?.clientSecret) {
        throw new Error('Jira OAuth configuration incomplete');
      }

      const tokenUrl = 'https://auth.atlassian.com/oauth/token';
      const params = new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: config.clientId,
        client_secret: config.clientSecret,
        code: code,
        redirect_uri: config.redirectUri,
      });

      const response = await fetch(tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      });

      if (!response.ok) {
        throw new Error('Failed to exchange authorization code for tokens');
      }

      const data = await response.json();

      // Fetch cloud ID
      const accessibleResourcesUrl = 'https://api.atlassian.com/oauth/token/accessible-resources';
      const resourcesResponse = await fetch(accessibleResourcesUrl, {
        headers: {
          Authorization: `Bearer ${data.access_token}`,
          Accept: 'application/json',
        },
      });

      if (!resourcesResponse.ok) {
        throw new Error('Failed to fetch accessible resources');
      }

      const resources = await resourcesResponse.json();
      const cloudId = resources[0]?.id;

      const updatedConfig: JiraConfig = {
        ...config,
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        tokenExpiresAt: Date.now() + data.expires_in * 1000,
        cloudId,
        baseUrl: cloudId ? `https://${cloudId}.atlassian.net` : undefined,
      };

      await saveConfig(updatedConfig);
    } catch (err) {
      console.error('Error handling OAuth callback:', err);
      throw err;
    }
  };

  const getAccessibleSites = async (clientId?: string): Promise<AccessibleSite[]> => {
    try {
      const getJiraDataFn = httpsCallable(functions, 'getJiraData');
      const result = await getJiraDataFn({
        action: 'resources',
        clientId,
      });
      return result.data as AccessibleSite[];
    } catch (err) {
      console.error('Error fetching accessible sites:', err);
      throw err;
    }
  };

  const getTickets = async (projectId: string, clientId?: string): Promise<JiraTicket[]> => {
    try {
      const getJiraDataFn = httpsCallable(functions, 'getJiraData');
      const result = await getJiraDataFn({
        action: 'tickets',
        projectId,
        clientId,
      });
      return result.data as JiraTicket[];
    } catch (err) {
      console.error('Error fetching Jira tickets:', err);
      throw err;
    }
  };

  const getCachedTickets = async (clientId: string): Promise<JiraTicket[]> => {
    try {
      const getJiraDataFn = httpsCallable(functions, 'getJiraData');
      const result = await getJiraDataFn({
        action: 'cachedTickets',
        clientId,
      });
      return result.data as JiraTicket[];
    } catch (err) {
      console.error('Error fetching cached Jira tickets:', err);
      throw err;
    }
  };

  const selectSite = async (site: AccessibleSite) => {
    if (!config) {
      throw new Error('No configuration available');
    }

    const updatedConfig: JiraConfig = {
      ...config,
      cloudId: site.id,
      baseUrl: site.url,
    };

    await saveConfig(updatedConfig);
  };

  const disconnect = async () => {
    try {
      const configRef = doc(db, 'config/jira');
      await updateDoc(configRef, {
        accessToken: null,
        refreshToken: null,
        tokenExpiresAt: null,
        cloudId: null,
        baseUrl: null,
      });
      await loadConfig();
    } catch (err) {
      console.error('Error disconnecting Jira:', err);
      throw err;
    }
  };

  const getProjects = async (clientId?: string): Promise<JiraProject[]> => {
    try {
      const getJiraDataFn = httpsCallable(functions, 'getJiraData');
      const result = await getJiraDataFn({
        action: 'projects',
        clientId,
      });
      return result.data as JiraProject[];
    } catch (err) {
      console.error('Error fetching Jira projects:', err);
      throw err;
    }
  };

  const linkClientToProject = async (clientId: string, projectId: string) => {
    try {
      const clientRef = doc(db, 'clients', clientId);
      await updateDoc(clientRef, {
        jiraProjectId: projectId,
      });
    } catch (err) {
      console.error('Error linking client to Jira project:', err);
      throw err;
    }
  };

  return {
    config,
    loading,
    error,
    saveConfig,
    initiateOAuthFlow,
    handleOAuthCallback,
    getAccessibleSites,
    selectSite,
    disconnect,
    getProjects,
    getTickets,
    getCachedTickets,
    linkClientToProject,
  };
}
